/**
 * FAQ content. First item renders defaultOpen for accessibility.
 * Voice rules: no em dashes, no banned words, Australian English.
 */
export const faqHead = {
  eyebrow: 'FAQ',
  heading: 'The questions every serious buyer asks us first.',
  lead: 'Didn\u2019t find yours?',
  leadLink: { label: 'Send it through', href: '#cta' },
  leadTrail: 'and we\u2019ll answer by the next business day.',
};

export const faqItems = [
  {
    q: 'Do you offer training as a standalone engagement, or only bundled with a build?',
    a:
      'Both. We deliver executive briefings, workforce fluency programmes, and champion development engagements for organisations that need their teams AI-ready without a system build attached. Our most successful clients combine the two. A system without adoption is an expensive data point, and training without application remains theoretical. The combined engagement is where measurable organisational capability emerges.',
    defaultOpen: true,
  },
  {
    q: 'How are training outcomes measured?',
    a:
      'Every programme is structured around measurable adoption outcomes. Tool utilisation rates, role-specific productivity benchmarks, and qualitative capability assessments captured at cohort commencement and at programme close. A post-programme report quantifies progression against baseline, with a 90-day follow-up measuring retention and sustained application.',
  },
  {
    q: 'How long does a typical engagement take?',
    a:
      'Single-system builds deliver in 2 to 3 weeks. Multi-system engagements run 6 to 8 weeks end-to-end, inclusive of the training cohort. Enterprise work is scoped against your governance process. Most delivery pods produce their first production workflow within 30 days, with training programmes running in parallel.',
  },
  {
    q: 'Do we own the automations once they are built?',
    a:
      'Yes. Every system is delivered with full source, documentation, and access hand-off. If you ever want to take the work in-house or to another vendor, nothing stops you. That is deliberate.',
  },
  {
    q: 'Where is our data hosted?',
    a:
      'Locally, by default. We architect toward data residency in your region wherever the stack allows, flag any exceptions up-front, and treat privacy and AI compliance as architecture decisions, not paperwork after the fact. For enterprise clients we can deploy into your own cloud tenant.',
  },
  {
    q: 'Everyone is selling AI agents. Do we actually need one?',
    a:
      'Often, no, and we tell you when an agent is the wrong answer. A deterministic system is cheaper to run, easier to audit, and behaves the same way every time. So we build the predictable machinery first and put agents only on the judgement calls that machinery cannot make, with your people approving the ones that matter. You get lower running costs, output you can trust, and control you keep.',
  },
  {
    q: 'Should we wait for the next model before investing?',
    a:
      'No. Models are already smart enough for most operational work, and have been for a while. What separates a production system from a demo is everything around the model: the context it is fed, the guardrails it runs inside, and the instrumentation that shows you what it did. A well-engineered system on today’s models beats a thin wrapper on next year’s, and when a stronger model lands we swap it in and your system gets faster or cheaper.',
  },
  {
    q: 'How do you price, and why scope-based?',
    a:
      'Hourly pricing punishes efficiency. We quote against a defined scope and outcomes, so you know the number before we start and we stay motivated to ship fast. Most projects are quoted within 48 hours of a discovery call.',
  },
  {
    q: 'What if we do not know what to automate yet?',
    a:
      'That is the most common starting point. The Discovery session is designed for exactly this. We audit your workflows, map the repetition, and send back a prioritised list of automations ranked by ROI.',
  },
  {
    q: 'What tools do you integrate with?',
    a:
      'The short answer: anything with an API or webhook. The longer answer includes Xero, MYOB, HubSpot, Salesforce, Pipedrive, Zendesk, Intercom, Gmail, Outlook, Slack, Teams, Monday, ClickUp, Airtable, Notion, and most Australian telco and calendaring stacks.',
  },
  {
    q: 'What differentiates your practice from a freelancer or generalist agency?',
    a:
      'Three things. First, we specialise. AI systems and enablement is the entire practice, not a service line appended to a web agency. Second, every engagement pairs a built system with the training required to operate it, because adoption is where most AI programmes fail. Third, we measure. Every deployment ships instrumented, with an impact and adoption dashboard, which is unusual in this category and tends to shift the conversation at the next board meeting.',
  },
  {
    q: 'Do you offer ongoing support after launch?',
    a:
      'Every engagement includes a post-launch support window. Beyond that we offer optional managed care. Monthly optimisation, performance tuning, and stack expansion on a light, month-to-month basis.',
  },
];
